import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { BadgeComponent, BadgeVariant, IconComponent, LoadingSkeletonComponent } from '@softline/ui-core';
import { CurrencyPipe } from '@angular/common';
import { CreditService } from '../../services/credit.service';
import { CustomerService } from '../../services/customer.service';
import { CreditView, toCreditView } from '../customer-info-side-panel/credit-view';
import { CustomerPanelInput } from '../customer-info-side-panel/customer-panel-input';

/** The "Kreditlimit" collapsible card — extracted from `CustomerInfoSidePanelComponent` so it can
 *  be mounted standalone (e.g. a document's main content area), not only inside the full customer
 *  overview panel. Self-contained: owns its own `CustomerService`/`CreditService` instances, same
 *  pattern as the panel it was extracted from. */
@Component({
  selector: 'soft-credit-limit-card',
  templateUrl: './credit-limit-card.component.html',
  styleUrl: './credit-limit-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, IconComponent, LoadingSkeletonComponent, CurrencyPipe],
  providers: [CustomerService, CreditService],
})
export class CreditLimitCardComponent {
  private readonly customerService = inject(CustomerService);
  private readonly creditService = inject(CreditService);

  readonly customer = input<CustomerPanelInput | null>(null);
  /** With a document loaded, the Kreditlimit section switches to the document-bound check — it
   *  reports the backend's assessment and keeps the current document out of the customer balance
   *  instead of counting it twice. */
  readonly documentId = input<string | null>(null);
  /** Any value that changes when the bound document changed (e.g. the last-saved timestamp) —
   *  refetches the credit limit, which goes stale with every position edit. */
  readonly documentRevision = input<unknown>(null);

  private readonly customerId = computed(() => {
    // Falsy ids (0/'') are placeholder values from document mappings, not real
    // customer-service ids — treat them as missing.
    const id = this.customer()?.id;
    return id ? String(id) : null;
  });

  /** Barverkauf/Einmalkunde: pools all walk-in customers, so it has no meaningful balance. */
  readonly isOneTimeCustomer = computed<boolean>(
    () => this.customer()?.oneTimeCustomer ?? false
  );

  /** Fields the customer detail carries itself — used while the credit endpoint has not answered
   *  (or is unavailable on this system). */
  private readonly customerFallback = computed(() => {
    const detail = this.customerService.customerDetail();
    if (!detail) return null;
    return {
      accountingBalance: detail.accountingBalance,
      creditFrame1: detail.creditFrame1,
      creditFrame2: detail.creditFrame2,
    };
  });

  private readonly syncCustomerId = effect(() => {
    this.customerService.customerId.set(this.customerId());
  });

  private readonly syncCreditParams = effect(() => {
    // A Barverkauf account pools every walk-in customer, so it has no meaningful
    // balance — the query is skipped, not just hidden.
    const suppressed = this.isOneTimeCustomer();
    this.creditService.customerId.set(suppressed ? null : this.customerId());
    this.creditService.documentId.set(suppressed ? null : this.documentId());
    this.creditService.revision.set(this.documentRevision());
  });

  readonly creditLoading = this.creditService.isLoading;

  readonly creditView = computed<CreditView | null>(() =>
    toCreditView(
      this.creditService.creditLimit(),
      this.creditService.creditBalance(),
      this.customerFallback()
    )
  );

  /** Traffic light for the section head — `level` is the backend's finished
      assessment, so it is rendered as-is. */
  readonly creditLevelBadge = computed<{
    variant: BadgeVariant;
    icon: string;
    label: string;
  } | null>(() => {
    switch (this.creditView()?.level) {
      case 'ok':
        return {
          variant: 'success',
          icon: 'fa-regular fa-circle-check',
          label: 'Im Rahmen',
        };
      case 'warning':
        return {
          variant: 'warning',
          icon: 'fa-regular fa-triangle-exclamation',
          label: 'Warnschwelle überschritten',
        };
      case 'limit':
        return {
          variant: 'danger',
          icon: 'fa-regular fa-circle-xmark',
          label: 'Limit überschritten',
        };
      default:
        return null;
    }
  });

  /** Explicit user override; absent = open once credit data has loaded. */
  private readonly expandedOverride = signal<boolean | null>(null);
  readonly expanded = computed(() => this.expandedOverride() ?? this.creditView() !== null);

  toggle(): void {
    this.expandedOverride.set(!this.expanded());
  }
}
